// redux/slices/contactSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api'; // Adjust path as needed

// ✅ Async thunk for contact form submission
export const submitContact = createAsyncThunk(
  'contact/submitContact',
  async (contactData, { rejectWithValue }) => {
    try {
      
      const response = await api.post('/contact', contactData);
      return response.data;
    } catch (error) {
      console.error('❌ Contact submission error:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to submit contact form'
      );
    }
  }
);

// ✅ Async thunk for newsletter subscription
export const subscribeNewsletter = createAsyncThunk(
  'contact/subscribeNewsletter',
  async (emailData, { rejectWithValue }) => {
    try {
      
      const response = await api.post('/contact/newsletter', emailData);
      return response.data;
    } catch (error) {
      console.error('❌ Newsletter subscription error:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to subscribe to newsletter'
      );
    }
  }
);

// ✅ Initial state
const initialState = {
  // Contact form states
  submitting: false,
  submitSuccess: false,
  error: null,
  message: null,
  lastSubmission: null,

  // Newsletter subscription states
  subscribeLoading: false,
  subscribeSuccess: false,
  subscribeError: null,
  subscribeMessage: null,

  // UI states
  showSuccessModal: false,
  formData: {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    propertyType: '',
    budget: '',
    timeline: '',
    preferredContact: 'email',
    propertyId: null
  }
};

// ✅ Contact slice
const contactSlice = createSlice({
  name: 'contact',
  initialState,
  reducers: {
    // Reset all contact state
    resetContactState: (state) => {
      state.submitting = false;
      state.subscribeLoading = false;
      state.submitSuccess = false;
      state.subscribeSuccess = false;
      state.error = null;
      state.subscribeError = null;
      state.message = null;
      state.subscribeMessage = null;
      state.showSuccessModal = false;
    },

    // Reset only contact form
    resetContactForm: (state) => {
      state.submitting = false;
      state.submitSuccess = false;
      state.error = null;
      state.message = null;
      state.showSuccessModal = false;
      state.formData = initialState.formData;
    },

    // Reset only newsletter subscription
    resetNewsletterSubscription: (state) => {
      state.subscribeLoading = false;
      state.subscribeSuccess = false;
      state.subscribeError = null;
      state.subscribeMessage = null;
    },

    // Update form data
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.subscribeError = null;
    },

    // Show/hide success modal
    setShowSuccessModal: (state, action) => {
      state.showSuccessModal = action.payload;
    }
  },
  extraReducers: (builder) => {
    // ✅ Contact form submission
    builder
      .addCase(submitContact.pending, (state) => {
        state.submitting = true;
        state.submitSuccess = false;
        state.error = null;
        state.message = null;
      })
      .addCase(submitContact.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.message = action.payload.message || 'Thank you for your message!';
        state.lastSubmission = action.payload.data?.contact || null;
        state.showSuccessModal = true;
        
        // Reset form data on successful submission
        state.formData = initialState.formData;
      })
      .addCase(submitContact.rejected, (state, action) => {
        state.submitting = false;
        state.submitSuccess = false;
        state.error = action.payload;
        state.message = null;
      })

    // ✅ Newsletter subscription
    builder
      .addCase(subscribeNewsletter.pending, (state) => {
        state.subscribeLoading = true;
        state.subscribeSuccess = false;
        state.subscribeError = null;
        state.subscribeMessage = null;
      })
      .addCase(subscribeNewsletter.fulfilled, (state, action) => {
        state.subscribeLoading = false;
        state.subscribeSuccess = true;
        state.subscribeMessage = action.payload.message || 'Successfully subscribed to newsletter!';
      })
      .addCase(subscribeNewsletter.rejected, (state, action) => {
        state.subscribeLoading = false;
        state.subscribeSuccess = false;
        state.subscribeError = action.payload;
        state.subscribeMessage = null;
      });
  }
});

// ✅ Export actions
export const {
  resetContactState,
  resetContactForm,
  resetNewsletterSubscription,
  updateFormData,
  clearErrors,
  setShowSuccessModal
} = contactSlice.actions;

// ✅ Selectors
export const selectContact = (state) => state.contact;
export const selectContactSubmitting = (state) => state.contact.submitting;
export const selectContactSuccess = (state) => state.contact.submitSuccess;
export const selectContactError = (state) => state.contact.error;
export const selectContactMessage = (state) => state.contact.message;
export const selectFormData = (state) => state.contact.formData;
export const selectShowSuccessModal = (state) => state.contact.showSuccessModal;

export const selectNewsletterLoading = (state) => state.contact.subscribeLoading;
export const selectNewsletterSuccess = (state) => state.contact.subscribeSuccess;
export const selectNewsletterError = (state) => state.contact.subscribeError;
export const selectNewsletterMessage = (state) => state.contact.subscribeMessage;

// ✅ Export reducer
export default contactSlice.reducer;
